<template>
  <div class="flex flex-wrap items-center gap-3">
    <!-- Status filter -->
    <MultiSelectDropdown
      id="filter-status"
      label="Status"
      :options="statusOptions"
      :model-value="selectedStatuses"
      @update:model-value="onStatusChange"
    />

    <!-- Executor filter -->
    <MultiSelectDropdown
      id="filter-executor"
      label="Executor"
      :options="executorOptions"
      :model-value="selectedExecutors"
      :disabled="executorOptions.length <= 1"
      :disabled-reason="executorOptions.length <= 1 ? 'All runners use the same executor' : undefined"
      @update:model-value="onExecutorChange"
    />

    <!-- Location filter -->
    <MultiSelectDropdown
      id="filter-location"
      label="Location"
      :options="locationOptions"
      :model-value="selectedLocations"
      :disabled="locationOptions.length <= 1"
      :disabled-reason="locationOptions.length <= 1 ? 'All runners are in the same location' : undefined"
      @update:model-value="onLocationChange"
    />

    <MultiSelectDropdown
      v-if="platformOptions.length > 1"
      id="filter-platform"
      label="Platform"
      :options="platformOptions"
      :model-value="selectedPlatforms"
      @update:model-value="emit('update:selectedPlatforms', $event)"
    />

    <!-- Search field -->
    <div class="relative">
      <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5">
        <svg class="size-4 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
        </svg>
      </div>
      <input
        ref="searchInput"
        id="filter-search"
        type="text"
        :value="searchQuery"
        @input="onSearchInput"
        @focus="searchFocused = true"
        @blur="searchFocused = false"
        placeholder="Search runners…"
        class="block w-48 rounded-md border border-gray-300 bg-white py-1.5 pl-8 pr-8 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-white/10 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-400 transition-colors"
      />
      <kbd
        v-if="!searchFocused && !searchQuery"
        class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2"
        aria-hidden="true"
      >
        <span class="inline-flex items-center justify-center rounded border border-gray-300 bg-gray-50 px-1.5 text-[11px] font-medium text-gray-400 dark:border-white/10 dark:bg-gray-700 dark:text-gray-500">/</span>
      </kbd>
      <button
        v-if="searchQuery"
        type="button"
        class="absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        aria-label="Clear search"
        @click="$emit('update:searchQuery', '')"
      >
        <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- Clear all button -->
    <button
      v-if="hasActiveFilters"
      type="button"
      class="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
      @click="clearAll"
    >
      <svg class="size-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
      Clear filters
    </button>
  </div>
</template>

<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted, ref} from 'vue';
import MultiSelectDropdown from '@/components/MultiSelectDropdown.vue';
import type {FilterOption} from '@/lib/runner-filters';

const searchInput = ref<HTMLInputElement | null>(null);
const searchFocused = ref(false);

function onSlashKey(e: KeyboardEvent) {
  if (e.key !== '/') return;
  const tag = (e.target as HTMLElement)?.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;
  e.preventDefault();
  searchInput.value?.focus();
}

onMounted(() => document.addEventListener('keydown', onSlashKey));
onBeforeUnmount(() => document.removeEventListener('keydown', onSlashKey));

const props = withDefaults(
  defineProps<{
    selectedStatuses: string[];
    selectedExecutors: string[];
    selectedLocations: string[];
    selectedPlatforms?: string[];
    searchQuery: string;
    statusOptions: FilterOption[];
    executorOptions: FilterOption[];
    locationOptions: FilterOption[];
    /** Shown only when the organization has runners of more than one platform. */
    platformOptions?: FilterOption[];
  }>(),
  { selectedPlatforms: () => [], platformOptions: () => [] }
);

const emit = defineEmits<{
  (e: 'update:selectedStatuses', value: string[]): void;
  (e: 'update:selectedExecutors', value: string[]): void;
  (e: 'update:selectedLocations', value: string[]): void;
  (e: 'update:selectedPlatforms', value: string[]): void;
  (e: 'update:searchQuery', value: string): void;
}>();

const hasActiveFilters = computed(
  () =>
    props.selectedStatuses.length > 0 ||
    props.selectedExecutors.length > 0 ||
    props.selectedLocations.length > 0 ||
    props.selectedPlatforms.length > 0 ||
    props.searchQuery.length > 0
);

function onStatusChange(value: string[]) {
  emit('update:selectedStatuses', value);
}

function onExecutorChange(value: string[]) {
  emit('update:selectedExecutors', value);
}

function onLocationChange(value: string[]) {
  emit('update:selectedLocations', value);
}

function onSearchInput(event: Event) {
  const value = (event.target as HTMLInputElement).value;
  emit('update:searchQuery', value);
}

function clearAll() {
  emit('update:selectedStatuses', []);
  emit('update:selectedExecutors', []);
  emit('update:selectedLocations', []);
  emit('update:selectedPlatforms', []);
  emit('update:searchQuery', '');
}
</script>
